import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ForecastModal from '../ForecastModal';

const TargetMatrix = () => {
  const { period } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [changes, setChanges] = useState({});
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [savedCells, setSavedCells] = useState({}); // Células salvas recentemente (para animação)
  const [savingCells, setSavingCells] = useState({}); // Células sendo salvas
  const [editingValues, setEditingValues] = useState({}); // Valores sendo digitados (texto bruto)
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    operation: '',
    unit: '',
    department: '',
    search: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    operations: [],
    units: [],
    departments: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  // Estados para Projeção e Resumo
  const [showForecastModal, setShowForecastModal] = useState(false);
  const [selectedGoalForForecast, setSelectedGoalForForecast] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryColumns, setSummaryColumns] = useState({
    showQuarterly: false,
    showSemesterly: false,
    showAnnually: false
  });
  
  // Ref para o timeout do debounce
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});

  const loadMatrixData = useCallback(async (params = {}, currentFilters = {}) => {
    try {
      setLoading(true);
      // Combinar params de navegação com filtros
      const allParams = { ...params };
      if (currentFilters.operation) allParams.operation = currentFilters.operation;
      if (currentFilters.unit) allParams.unit = currentFilters.unit;
      if (currentFilters.department) allParams.department = currentFilters.department;
      if (currentFilters.search) allParams.search = currentFilters.search;

      const queryString = new URLSearchParams(
        Object.fromEntries(Object.entries(allParams).filter(([_, v]) => v !== '' && v !== 0 && v !== undefined))
      ).toString();
      const url = `/matrix/${period}${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      
      if (response.data.success) {
        setMatrixData(response.data.data);
        // Atualizar opções de filtro (apenas se não houver filtros aplicados, para manter opções completas)
        if (response.data.data.filter_options && !currentFilters.operation && !currentFilters.unit && !currentFilters.department && !currentFilters.search) {
          setFilterOptions(response.data.data.filter_options);
        }
      }
    } catch (error) {
      console.error('Error loading matrix data:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados da matriz' });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    // Parse query params from URL
    const paramsObj = {
      year: parseInt(searchParams.get('year')) || 0,
      month: parseInt(searchParams.get('month')) || 0,
      week: parseInt(searchParams.get('week')) || 0
    };
    loadMatrixData(paramsObj, filters);
  }, [period, searchParams, loadMatrixData, filters]);

  // Carregar opções de filtros (sem filtros aplicados) na montagem
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const paramsObj = {
          year: parseInt(searchParams.get('year')) || 0,
          month: parseInt(searchParams.get('month')) || 0,
          week: parseInt(searchParams.get('week')) || 0
        };
        const queryString = new URLSearchParams(
          Object.fromEntries(Object.entries(paramsObj).filter(([_, v]) => v !== 0))
        ).toString();
        const url = `/matrix/${period}${queryString ? `?${queryString}` : ''}`;
        const response = await axios.get(url);
        if (response.data.success && response.data.data.filter_options) {
          setFilterOptions(response.data.data.filter_options);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Função para salvar uma única alteração
  const saveChange = async (changeData, cellKey) => {
    try {
      // Marcar célula como salvando
      setSavingCells(prev => ({ ...prev, [cellKey]: true }));
      setSaving(true);
      
      const response = await axios.post('/matrix/update', {
        matrix_data: [changeData]
      });

      if (response.data.success) {
        // Atualizar dados locais para refletir a mudança
        // Estrutura: matrix_data → operations → departments → goals → targets
        setMatrixData(prev => {
          if (!prev || !prev.matrix_data) return prev;
          
          const newData = { 
            ...prev,
            matrix_data: prev.matrix_data.map(operation => ({
              ...operation,
              departments: (operation.departments || []).map(department => ({
                ...department,
                goals: (department.goals || []).map(goal => {
                  if (goal.parent_goal_id === changeData.parent_goal_id) {
                    return {
                      ...goal,
                      targets: (goal.targets || []).map(target => {
                        if (target.date === changeData.date) {
                          const newTarget = { ...target };
                          
                          // Atualizar target (meta)
                          if (changeData.target !== undefined) {
                            newTarget.target = changeData.target; // pode ser null
                          }
                          
                          // Atualizar result (realizado)
                          if (changeData.result !== undefined) {
                            if (changeData.result === null) {
                              // Valor apagado - limpar completed_goal
                              newTarget.completed_goal = null;
                              newTarget.is_completed = false;
                            } else {
                              // Criar ou atualizar completed_goal
                              const targetForCalc = changeData.target !== undefined ? changeData.target : newTarget.target;
                              newTarget.completed_goal = {
                                ...(newTarget.completed_goal || {}),
                                actual_value: changeData.result,
                                completion_percentage: targetForCalc > 0 
                                  ? Math.round((changeData.result / targetForCalc) * 1000) / 10 
                                  : 0
                              };
                              newTarget.is_completed = true;
                            }
                          }
                          return newTarget;
                        }
                        return target;
                      })
                    };
                  }
                  return goal;
                })
              }))
            }))
          };
          return newData;
        });
        
        // Marcar célula como salva (animação verde)
        setSavedCells(prev => ({ ...prev, [cellKey]: true }));
        
        // Remover animação após 2 segundos
        setTimeout(() => {
          setSavedCells(prev => {
            const newState = { ...prev };
            delete newState[cellKey];
            return newState;
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error auto-saving:', error);
      setMessage({ 
        type: 'error', 
        text: 'Erro ao salvar' 
      });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSavingCells(prev => {
        const newState = { ...prev };
        delete newState[cellKey];
        return newState;
      });
      setSaving(false);
    }
  };

  // Atualiza apenas o texto sendo digitado (sem parsear)
  const handleInputChange = (parentGoalId, date, field, value) => {
    const editKey = `${parentGoalId}_${date}_${field}`;
    setEditingValues(prev => ({
      ...prev,
      [editKey]: value
    }));
  };
  
  // Processa o valor quando o usuário para de digitar ou sai do campo
  const handleTargetChange = (parentGoalId, date, field, value, goalId = null) => {
    const key = `${parentGoalId}_${date}`;
    
    // Tratar valor vazio ou inválido
    let parsedValue = null;
    if (value && value.trim() !== '') {
      const parsed = parseFloat(value.replace(/\./g, '').replace(',', '.'));
      parsedValue = isNaN(parsed) ? null : parsed;
    }
    
    // Atualizar estado local
    const existing = pendingChangesRef.current[key] || { parent_goal_id: parentGoalId, date };
    const updated = {
      ...existing,
      [field]: parsedValue
    };
    if (goalId) {
      updated.goal_id = goalId;
    }
    pendingChangesRef.current[key] = updated;
    
    setChanges(prev => ({
      ...prev,
      [key]: updated
    }));
    
    // Auto-save com debounce (salva 2 segundos após parar de digitar)
    if (autoSaveEnabled) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        const changeToSave = pendingChangesRef.current[key];
        if (changeToSave && (changeToSave.target !== undefined || changeToSave.result !== undefined)) {
          saveChange(changeToSave, key);
          // Remover do pending após salvar
          delete pendingChangesRef.current[key];
          setChanges(prev => {
            const newChanges = { ...prev };
            delete newChanges[key];
            return newChanges;
          });
          // Limpar texto de edição
          setEditingValues(prev => {
            const newState = { ...prev };
            delete newState[`${changeToSave.parent_goal_id}_${changeToSave.date}_target`];
            delete newState[`${changeToSave.parent_goal_id}_${changeToSave.date}_result`];
            return newState;
          });
        }
      }, 2000); // 2 segundos de debounce - mais tempo para digitar
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearchChange = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
    }, 500);
  };

  const clearFilters = () => {
    setFilters({ operation: '', unit: '', department: '', search: '' });
  };

  const activeFilterCount = [filters.operation, filters.unit, filters.department, filters.search].filter(Boolean).length;

  const handleSave = async () => {
    try {
      setSaving(true);
      const changesArray = Object.values(changes);
      
      if (changesArray.length === 0) {
        setMessage({ type: 'info', text: 'Nenhuma alteração para salvar' });
        return;
      }

      const response = await axios.post('/matrix/update', {
        matrix_data: changesArray
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message || 'Alterações salvas com sucesso!' 
        });
        setChanges({});
        pendingChangesRef.current = {};
        // Reload data
        const paramsObj = {
          year: parseInt(searchParams.get('year')) || 0,
          month: parseInt(searchParams.get('month')) || 0,
          week: parseInt(searchParams.get('week')) || 0
        };
        loadMatrixData(paramsObj, filters);
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao salvar alterações' 
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };
  
  // Limpar timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleNavigation = (offsetType, offsetValue) => {
    const currentParams = {
      year: parseInt(searchParams.get('year')) || 0,
      month: parseInt(searchParams.get('month')) || 0,
      week: parseInt(searchParams.get('week')) || 0
    };
    
    const newParams = { ...currentParams };
    newParams[offsetType] = (newParams[offsetType] || 0) + offsetValue;
    
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(newParams).filter(([_, v]) => v !== 0)
      )
    ).toString();
    
    navigate(`/matrix/${period}${queryString ? `?${queryString}` : ''}`);
  };

  const formatBrazilianNumber = (value) => {
    if (!value && value !== 0) return '';
    const num = typeof value === 'string' 
      ? parseFloat(value.replace(/\./g, '').replace(',', '.')) 
      : value;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInputValue = (value) => {
    if (value === null || value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) {
      return '';
    }
    return formatBrazilianNumber(value);
  };

  // Função para calcular resumo de uma meta para um range de períodos
  const calculateGoalSummary = (goalData, startIdx, endIdx) => {
    let totalTarget = 0;
    let totalResult = 0;
    let countTargets = 0;
    let countResults = 0;
    
    for (let i = startIdx; i <= endIdx && i < (goalData.targets?.length || 0); i++) {
      const target = goalData.targets[i];
      if (target) {
        if (target.target !== null && target.target !== undefined) {
          totalTarget += target.target;
          countTargets++;
        }
        if (target.completed_goal?.actual_value !== null && target.completed_goal?.actual_value !== undefined) {
          totalResult += target.completed_goal.actual_value;
          countResults++;
        }
      }
    }
    
    const percentage = totalTarget > 0 ? (totalResult / totalTarget) * 100 : 0;
    return { totalTarget, totalResult, percentage, countResults, countTargets };
  };

  // Gerar headers estendidos com colunas de resumo
  const getExtendedHeaders = () => {
    if (!matrixData?.period_headers) return [];
    
    const headers = [];
    matrixData.period_headers.forEach((header, idx) => {
      headers.push({ ...header, type: 'period', originalIdx: idx });
      
      const monthNum = idx + 1;
      
      // Trimestral: após 3, 6, 9, 12
      if (summaryColumns.showQuarterly && [3, 6, 9, 12].includes(monthNum)) {
        const qNum = Math.ceil(monthNum / 3);
        headers.push({ 
          label: `${qNum}º Tri`, 
          type: 'summary', 
          summaryType: 'quarterly',
          startIdx: (qNum - 1) * 3, 
          endIdx: qNum * 3 - 1,
          bgColor: '#cfe2ff'
        });
      }
      
      // Semestral: após 6, 12
      if (summaryColumns.showSemesterly && [6, 12].includes(monthNum)) {
        const sNum = monthNum === 6 ? 1 : 2;
        headers.push({ 
          label: `${sNum}º Sem`, 
          type: 'summary', 
          summaryType: 'semesterly',
          startIdx: (sNum - 1) * 6, 
          endIdx: sNum * 6 - 1,
          bgColor: '#d1e7dd'
        });
      }
      
      // Anual: após 12
      if (summaryColumns.showAnnually && monthNum === 12) {
        headers.push({ 
          label: 'Anual', 
          type: 'summary', 
          summaryType: 'annually',
          startIdx: 0, 
          endIdx: 11,
          bgColor: '#fff3cd'
        });
      }
    });
    
    return headers;
  };

  const extendedHeaders = getExtendedHeaders();

  // Função para exportar a matriz para Excel
  const handleExportExcel = () => {
    if (!matrixData?.matrix_data || !matrixData?.period_headers) return;

    const headers = extendedHeaders;
    const rows = [];

    // Header row: Operação | Departamento | Meta | Tipo | período1 | período2 | ...
    const headerRow = ['Operação', 'Departamento', 'Meta', 'Tipo'];
    headers.forEach(h => {
      headerRow.push(h.label);
    });
    rows.push(headerRow);

    // Data rows - cada meta gera 3 linhas: Meta, Real, %
    matrixData.matrix_data.forEach(operationData => {
      operationData.departments?.forEach(departmentData => {
        departmentData.goals?.forEach(goalData => {
          const baseInfo = [
            operationData.operation_key,
            departmentData.department_key,
            goalData.goal?.title || ''
          ];

          // Linha 1: Meta (target)
          const targetRow = [...baseInfo, 'Meta'];
          headers.forEach(header => {
            if (header.type === 'summary') {
              const summary = calculateGoalSummary(goalData, header.startIdx, header.endIdx);
              targetRow.push(summary.totalTarget || '');
            } else {
              const targetData = goalData.targets?.[header.originalIdx];
              targetRow.push(targetData?.target ?? '');
            }
          });
          rows.push(targetRow);

          // Linha 2: Real (resultado)
          const resultRow = ['', '', '', 'Real'];
          headers.forEach(header => {
            if (header.type === 'summary') {
              const summary = calculateGoalSummary(goalData, header.startIdx, header.endIdx);
              resultRow.push(summary.countResults > 0 ? summary.totalResult : '');
            } else {
              const targetData = goalData.targets?.[header.originalIdx];
              resultRow.push(targetData?.completed_goal?.actual_value ?? '');
            }
          });
          rows.push(resultRow);

          // Linha 3: Percentual
          const pctRow = ['', '', '', '%'];
          headers.forEach(header => {
            if (header.type === 'summary') {
              const summary = calculateGoalSummary(goalData, header.startIdx, header.endIdx);
              pctRow.push(summary.countResults > 0 ? Math.round(summary.percentage * 10) / 10 : '');
            } else {
              const targetData = goalData.targets?.[header.originalIdx];
              if (targetData) {
                const tVal = targetData.target;
                const rVal = targetData.completed_goal?.actual_value;
                const pct = tVal > 0 && rVal != null
                  ? Math.round((rVal / tVal) * 1000) / 10
                  : '';
                pctRow.push(pct);
              } else {
                pctRow.push('');
              }
            }
          });
          rows.push(pctRow);
        });
      });
    });

    // Criar workbook
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Ajustar largura das colunas
    const colWidths = headerRow.map((h, i) => {
      if (i === 0) return { wch: 25 };
      if (i === 1) return { wch: 20 };
      if (i === 2) return { wch: 35 };
      if (i === 3) return { wch: 6 };
      return { wch: 14 };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    const sheetName = `Matriz ${matrixData.period_label || period}`.substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Gerar nome do arquivo
    const fileName = `Matriz_Metas_${matrixData.period_label || period}_${matrixData.navigation?.current_label || ''}.xlsx`
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_.-]/g, '');

    XLSX.writeFile(wb, fileName);
  };

  if (loading) {
    return (
      <div className="goals-list-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!matrixData) {
    return (
      <div className="goals-list-page">
        <div className="alert alert-warning">
          Nenhum dado disponível para este período.
        </div>
      </div>
    );
  }

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="goals-list-page">
      <div className="container-fluid py-4">
        {message && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'error' ? 'danger' : 'info'} alert-dismissible fade show`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="h4 mb-2 fw-bold">
              <i className="fas fa-table me-2 text-primary"></i>
              Matriz de Metas - {matrixData.period_label}
            </h1>
            <p className="mb-0 fs-6" style={{ color: '#4b5563' }}>Gerencie as metas por período de forma inteligente</p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {/* Toggle Auto-save */}
            <div className="form-check form-switch me-2" title="Salva automaticamente 1.5s após digitar">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="autoSaveToggle"
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="autoSaveToggle">
                Auto-save
              </label>
            </div>
            
            {/* Botão Exportar Excel */}
            <button 
              className="btn btn-success me-2"
              onClick={handleExportExcel}
              title="Exportar matriz para Excel"
              disabled={!matrixData?.matrix_data?.length}
            >
              <i className="fas fa-file-excel me-1"></i>Excel
            </button>
            
            {/* Botão Resumo Geral */}
            <button 
              className={`btn ${(summaryColumns.showQuarterly || summaryColumns.showSemesterly || summaryColumns.showAnnually) ? 'btn-success' : 'btn-info text-white'} me-2`}
              onClick={() => setShowSummaryModal(true)}
              title="Configurar colunas de resumo"
            >
              <i className="fas fa-table me-1"></i>Resumo
            </button>
            
            <Link to="/matrix" className="btn btn-outline-secondary">
              <i className="fas fa-arrow-left me-1"></i>Voltar
            </Link>
            
            {/* Botão manual só aparece se auto-save desligado ou tem alterações pendentes */}
            {(!autoSaveEnabled || hasChanges) && (
              <button 
                className="btn btn-success" 
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <i className="fas fa-save me-1"></i>
                {hasChanges ? `Salvar (${Object.keys(changes).length})` : 'Salvar'}
              </button>
            )}
          </div>
        </div>

        {/* Barra de Filtros */}
        <div className="card mb-3">
          <div 
            className="card-header py-2 d-flex justify-content-between align-items-center" 
            style={{ cursor: 'pointer', backgroundColor: activeFilterCount > 0 ? '#e8f4fd' : '#f8f9fa' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="d-flex align-items-center gap-2">
              <i className={`fas fa-filter ${activeFilterCount > 0 ? 'text-primary' : 'text-muted'}`}></i>
              <span className="fw-bold small">Filtros</span>
              {activeFilterCount > 0 && (
                <span className="badge bg-primary rounded-pill">{activeFilterCount}</span>
              )}
            </div>
            <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'} text-muted`}></i>
          </div>
          {showFilters && (
            <div className="card-body py-2">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label small mb-1">Operação</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.operation}
                    onChange={(e) => handleFilterChange('operation', e.target.value)}
                  >
                    <option value="">Todas</option>
                    {filterOptions.operations.map(op => (
                      <option key={op.code} value={op.code}>{op.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Unidade</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.unit}
                    onChange={(e) => handleFilterChange('unit', e.target.value)}
                  >
                    <option value="">Todas</option>
                    {filterOptions.units.map(u => (
                      <option key={u.code} value={u.code}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Departamento</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.department}
                    onChange={(e) => handleFilterChange('department', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {filterOptions.departments.map(d => (
                      <option key={d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Buscar</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><i className="fas fa-search"></i></span>
                    <input 
                      type="text" 
                      className="form-control"
                      placeholder="Título da meta..."
                      defaultValue={filters.search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <div className="col-12 mt-1">
                    <button 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={clearFilters}
                    >
                      <i className="fas fa-times me-1"></i>Limpar filtros
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="row mb-3">
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center py-2">
                <div className="me-3">
                  <i className="fas fa-bullseye text-primary fs-3"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="text-primary mb-0 fw-bold fs-4">{matrixData.total_goals}</div>
                  <div className="small text-muted">Metas</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center py-2">
                <div className="me-3">
                  <i className="fas fa-calendar-alt text-info fs-3"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="text-info mb-0 fw-bold fs-4">{matrixData.total_periods}</div>
                  <div className="small text-muted">Períodos</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center py-2">
                <div className="me-3">
                  <i className="fas fa-check-circle text-success fs-3"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="text-success mb-0 fw-bold fs-4">{matrixData.total_targets_defined}</div>
                  <div className="small text-muted">Metas Definidas</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center py-2">
                <div className="me-3">
                  <i className="fas fa-chart-pie text-warning fs-3"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="text-warning mb-0 fw-bold fs-4">{matrixData.fill_percentage}%</div>
                  <div className="small text-muted">% Preenchimento</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Matrix Table */}
        {matrixData.matrix_data && matrixData.matrix_data.length > 0 ? (
          <div className="card mb-3">
            <div className="card-header text-white bg-primary">
              <h5 className="mb-0">
                <i className="fas fa-th-large me-2"></i>
                Matriz de Planejamento - {matrixData.navigation?.current_label || ''}
              </h5>
            </div>
            <div className="card-body p-0">
              {/* Navigation */}
              {matrixData.navigation?.show_navigation && (
                <div className="p-3 bg-light border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const offsetType = period === 'daily' ? 'week' : period === 'weekly' ? 'month' : 'year';
                        handleNavigation(offsetType, -1);
                      }}
                      title={period === 'daily' ? 'Semana Anterior' : 
                             period === 'weekly' ? 'Mês Anterior' : 'Ano Anterior'}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    
                    <div className="text-center">
                      <h6 className="mb-0 fw-bold">{matrixData.navigation.current_label}</h6>
                    </div>
                    
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const offsetType = period === 'daily' ? 'week' : period === 'weekly' ? 'month' : 'year';
                        handleNavigation(offsetType, 1);
                      }}
                      title={period === 'daily' ? 'Próxima Semana' : 
                             period === 'weekly' ? 'Próximo Mês' : 'Próximo Ano'}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Table */}
              <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <table className="table table-bordered mb-0" style={{ minWidth: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 12 }}>
                    <tr>
                      <th style={{ 
                        minWidth: '250px', 
                        position: 'sticky', 
                        left: 0, 
                        background: '#fff', 
                        zIndex: 13,
                        boxShadow: '1px 0 2px rgba(0,0,0,0.1)'
                      }}>
                        Meta
                      </th>
                      {extendedHeaders.map((header, idx) => (
                        <th 
                          key={idx} 
                          style={{ 
                            minWidth: header.type === 'summary' ? '100px' : '120px', 
                            textAlign: 'center', 
                            background: header.type === 'summary' ? header.bgColor : '#fff',
                            fontWeight: header.type === 'summary' ? 600 : 'normal'
                          }}
                        >
                          {header.type === 'summary' && <i className="fas fa-chart-pie me-1" style={{ fontSize: '0.7rem' }}></i>}
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixData.matrix_data.map((operationData, opIdx) => (
                      <React.Fragment key={opIdx}>
                        {/* Operation Header - Sticky */}
                        <tr className="table-secondary operation-header-sticky">
                          <td 
                            colSpan={extendedHeaders.length + 1}
                            style={{ 
                              fontWeight: 600, 
                              padding: '0.75rem 1rem',
                              position: 'sticky',
                              top: '41px', /* Abaixo do header da tabela */
                              zIndex: 10, /* Acima das células sticky (z-index 9) */
                              backgroundColor: '#e9ecef',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                            }}
                          >
                            <i className="fas fa-building me-2"></i>
                            <strong>{operationData.operation_key}</strong>
                          </td>
                        </tr>
                        
                        {operationData.departments?.map((departmentData, deptIdx) => (
                          <React.Fragment key={deptIdx}>
                            {/* Department Header */}
                            <tr className="table-light">
                              <td 
                                colSpan={extendedHeaders.length + 1}
                                style={{ fontWeight: 600, padding: '0.75rem 1rem', paddingLeft: '2rem' }}
                              >
                                <i className="fas fa-users me-2"></i>
                                <strong>{departmentData.department_key}</strong>
                                <span className="badge bg-secondary ms-2">
                                  {departmentData.goals?.length || 0} metas
                                </span>
                              </td>
                            </tr>
                            
                            {/* Goals */}
                            {departmentData.goals?.map((goalData, goalIdx) => (
                              <tr key={goalIdx}>
                                <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 9, minWidth: '250px' }}>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <Link 
                                        to={`/goals/${goalData.parent_goal_id}/targets`}
                                        className="fw-bold text-decoration-none text-dark"
                                        title="Ver matriz de targets desta meta"
                                      >
                                        {goalData.goal?.title}
                                      </Link>
                                      <small className="text-muted d-block">{goalData.goal?.unit}</small>
                                    </div>
                                    <button
                                      className="btn btn-link btn-sm p-0 text-primary"
                                      title="Projetar metas"
                                      onClick={async () => {
                                        try {
                                          // Buscar dados completos da meta para projeção
                                          const response = await axios.get(`/goals/${goalData.parent_goal_id}/targets`);
                                          if (response.data.success) {
                                            const goalMatrix = response.data.data;
                                            setSelectedGoalForForecast({
                                              id: goalData.parent_goal_id,
                                              parent_goal_id: goalData.parent_goal_id,
                                              title: goalData.goal?.title,
                                              period: goalMatrix.goal?.period || period,
                                              years: goalMatrix.years || [],
                                              current_year: goalMatrix.current_year || new Date().getFullYear(),
                                              matrix: goalMatrix.matrix || []
                                            });
                                            setShowForecastModal(true);
                                          }
                                        } catch (error) {
                                          console.error('Erro ao carregar dados da meta:', error);
                                          alert('Erro ao carregar dados da meta para projeção');
                                        }
                                      }}
                                    >
                                      <i className="fas fa-chart-line"></i>
                                    </button>
                                  </div>
                                </td>
                                {extendedHeaders.map((header, headerIdx) => {
                                  // Célula de resumo
                                  if (header.type === 'summary') {
                                    const summary = calculateGoalSummary(goalData, header.startIdx, header.endIdx);
                                    const pct = summary.percentage;
                                    return (
                                      <td 
                                        key={`summary-${headerIdx}`}
                                        style={{ 
                                          textAlign: 'center', 
                                          padding: '0.25rem', 
                                          minWidth: '100px',
                                          backgroundColor: header.bgColor,
                                          verticalAlign: 'middle'
                                        }}
                                      >
                                        <div style={{ fontSize: '0.7rem' }}>
                                          <div><strong>M:</strong> {formatBrazilianNumber(summary.totalTarget)}</div>
                                          <div><strong>R:</strong> {summary.countResults > 0 ? formatBrazilianNumber(summary.totalResult) : '-'}</div>
                                          {summary.countResults > 0 && (
                                            <div className={`fw-bold ${pct >= 100 ? 'text-success' : pct >= 80 ? 'text-warning' : 'text-danger'}`}>
                                              {pct.toFixed(1)}%
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    );
                                  }
                                  
                                  // Célula de período normal
                                  const targetIdx = header.originalIdx;
                                  const targetData = goalData.targets?.[targetIdx];
                                  if (!targetData) return <td key={headerIdx}></td>;
                                  
                                  const changeKey = `${goalData.parent_goal_id}_${targetData.date}`;
                                  const targetEditKey = `${goalData.parent_goal_id}_${targetData.date}_target`;
                                  const resultEditKey = `${goalData.parent_goal_id}_${targetData.date}_result`;
                                  const hasChange = changes[changeKey];
                                  const isSaving = savingCells[changeKey];
                                  const isSaved = savedCells[changeKey];
                                  
                                  // Valores de Meta - usa texto bruto se está editando
                                  const isEditingTarget = editingValues[targetEditKey] !== undefined;
                                  const targetDisplayValue = isEditingTarget
                                    ? editingValues[targetEditKey]
                                    : (hasChange?.target !== undefined
                                        ? formatInputValue(hasChange.target)
                                        : formatInputValue(targetData.target));
                                  const hasTargetChange = hasChange?.target !== undefined || isEditingTarget;
                                  
                                  // Valores de Resultado - usa texto bruto se está editando
                                  const actualValue = targetData.completed_goal?.actual_value;
                                  const isEditingResult = editingValues[resultEditKey] !== undefined;
                                  const resultDisplayValue = isEditingResult
                                    ? editingValues[resultEditKey]
                                    : (hasChange?.result !== undefined
                                        ? formatInputValue(hasChange.result)
                                        : formatInputValue(actualValue));
                                  const hasResultChange = hasChange?.result !== undefined || isEditingResult;
                                  
                                  // Calcular percentual
                                  const targetVal = hasChange?.target !== undefined ? hasChange.target : targetData.target;
                                  const resultVal = hasChange?.result !== undefined ? hasChange.result : actualValue;
                                  const percentage = targetVal > 0 && resultVal != null
                                    ? Math.round((resultVal / targetVal) * 1000) / 10
                                    : null;
                                  
                                  // Determinar estilo da célula baseado no estado
                                  const getCellStyle = () => {
                                    if (isSaved) return { 
                                      textAlign: 'center', 
                                      padding: '0.25rem', 
                                      minWidth: '120px',
                                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                      transition: 'background-color 0.5s ease'
                                    };
                                    if (isSaving) return { 
                                      textAlign: 'center', 
                                      padding: '0.25rem', 
                                      minWidth: '120px',
                                      backgroundColor: 'rgba(251, 191, 36, 0.15)',
                                      transition: 'background-color 0.3s ease'
                                    };
                                    return { textAlign: 'center', padding: '0.25rem', minWidth: '120px' };
                                  };
                                  
                                  // Determinar borda do input
                                  const getInputBorder = (isTarget) => {
                                    if (isSaved) return '2px solid #10b981';
                                    if (isSaving) return '2px solid #fbbf24';
                                    if (isTarget && hasTargetChange) return '2px solid #c5a572';
                                    if (!isTarget && hasResultChange) return '2px solid #10b981';
                                    return '1px solid #dee2e6';
                                  };
                                  
                                  return (
                                    <td 
                                      key={headerIdx}
                                      className={`${isSaved ? '' : targetData.is_completed ? 'table-success' : targetData.target ? 'table-warning' : ''}`}
                                      style={getCellStyle()}
                                    >
                                      {/* Indicador de salvando/salvo */}
                                      {(isSaving || isSaved) && (
                                        <div style={{ 
                                          fontSize: '0.65rem', 
                                          marginBottom: '2px',
                                          color: isSaved ? '#10b981' : '#fbbf24',
                                          fontWeight: 'bold'
                                        }}>
                                          {isSaving ? '⏳ Salvando...' : '✓ Salvo!'}
                                        </div>
                                      )}
                                      
                                      {/* Campo Meta */}
                                      <div className="mb-1">
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={targetDisplayValue}
                                          onChange={(e) => {
                                            // Atualiza texto imediatamente (sem parsear)
                                            handleInputChange(goalData.parent_goal_id, targetData.date, 'target', e.target.value);
                                            // Processa com debounce
                                            handleTargetChange(goalData.parent_goal_id, targetData.date, 'target', e.target.value);
                                          }}
                                          onBlur={(e) => {
                                            // Formata ao sair do campo
                                            const editKey = `${goalData.parent_goal_id}_${targetData.date}_target`;
                                            if (editingValues[editKey] !== undefined) {
                                              setEditingValues(prev => {
                                                const newState = { ...prev };
                                                delete newState[editKey];
                                                return newState;
                                              });
                                            }
                                          }}
                                          placeholder="Meta"
                                          disabled={isSaving}
                                          style={{ 
                                            textAlign: 'center',
                                            fontSize: '0.75rem',
                                            padding: '0.2rem 0.4rem',
                                            border: getInputBorder(true),
                                            transition: 'border-color 0.3s ease'
                                          }}
                                        />
                                      </div>
                                      
                                      {/* Campo Resultado */}
                                      <div>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={resultDisplayValue}
                                          onChange={(e) => {
                                            // Atualiza texto imediatamente (sem parsear)
                                            handleInputChange(goalData.parent_goal_id, targetData.date, 'result', e.target.value);
                                            // Processa com debounce
                                            handleTargetChange(
                                              goalData.parent_goal_id, 
                                              targetData.date, 
                                              'result', 
                                              e.target.value,
                                              targetData.completed_goal?.goal_id
                                            );
                                          }}
                                          onBlur={(e) => {
                                            // Formata ao sair do campo
                                            const editKey = `${goalData.parent_goal_id}_${targetData.date}_result`;
                                            if (editingValues[editKey] !== undefined) {
                                              setEditingValues(prev => {
                                                const newState = { ...prev };
                                                delete newState[editKey];
                                                return newState;
                                              });
                                            }
                                          }}
                                          placeholder="Real"
                                          disabled={isSaving}
                                          style={{ 
                                            textAlign: 'center',
                                            fontSize: '0.75rem',
                                            padding: '0.2rem 0.4rem',
                                            backgroundColor: '#f8f9fa',
                                            border: getInputBorder(false),
                                            transition: 'border-color 0.3s ease'
                                          }}
                                        />
                                      </div>
                                      
                                      {/* Percentual */}
                                      {percentage !== null && (
                                        <div className="mt-1">
                                          <small className={`fw-bold ${
                                            percentage >= 100 ? 'text-success' :
                                            percentage >= 80 ? 'text-warning' :
                                            'text-danger'
                                          }`}>
                                            {percentage}%
                                          </small>
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-warning">
            Nenhuma meta encontrada para este período.
          </div>
        )}

        {/* Modal de Projeção */}
        <ForecastModal
          show={showForecastModal}
          onClose={() => {
            setShowForecastModal(false);
            setSelectedGoalForForecast(null);
          }}
          goalData={selectedGoalForForecast}
          onForecastApplied={(changesToSave, targetYear) => {
            // Atualizar dados localmente sem recarregar a página
            if (changesToSave && selectedGoalForForecast) {
              setMatrixData(prev => {
                if (!prev || !prev.matrix_data) return prev;
                
                // Criar mapa de mudanças por data
                const changesMap = {};
                changesToSave.forEach(change => {
                  changesMap[change.date] = change.target;
                });
                
                return {
                  ...prev,
                  matrix_data: prev.matrix_data.map(operationData => ({
                    ...operationData,
                    departments: operationData.departments?.map(deptData => ({
                      ...deptData,
                      goals: deptData.goals?.map(goalData => {
                        if (goalData.parent_goal_id === selectedGoalForForecast.parent_goal_id) {
                          return {
                            ...goalData,
                            targets: goalData.targets?.map(target => {
                              if (changesMap[target.date] !== undefined) {
                                return {
                                  ...target,
                                  target: changesMap[target.date]
                                };
                              }
                              return target;
                            })
                          };
                        }
                        return goalData;
                      })
                    }))
                  }))
                };
              });
              
              // Mostrar mensagem de sucesso
              setMessage({ type: 'success', text: 'Projeção aplicada com sucesso!' });
              setTimeout(() => setMessage(null), 3000);
            }
          }}
        />

        {/* Modal Simplificado de Resumo - Apenas Opções */}
        {showSummaryModal && (
          <div 
            className="modal show d-block" 
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && setShowSummaryModal(false)}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-table me-2"></i>
                    Colunas de Resumo
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowSummaryModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-3">
                    Selecione quais colunas de resumo deseja exibir na matriz:
                  </p>
                  <div className="d-flex flex-column gap-3">
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="summaryQuarterly"
                        checked={summaryColumns.showQuarterly}
                        onChange={(e) => setSummaryColumns(prev => ({ ...prev, showQuarterly: e.target.checked }))}
                      />
                      <label className="form-check-label text-dark" htmlFor="summaryQuarterly">
                        <i className="fas fa-chart-pie me-2 text-primary"></i>
                        <strong className="text-dark">Trimestral</strong>
                        <small className="d-block text-secondary">Colunas após Mar, Jun, Set e Dez</small>
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="summarySemesterly"
                        checked={summaryColumns.showSemesterly}
                        onChange={(e) => setSummaryColumns(prev => ({ ...prev, showSemesterly: e.target.checked }))}
                      />
                      <label className="form-check-label text-dark" htmlFor="summarySemesterly">
                        <i className="fas fa-calendar-alt me-2 text-success"></i>
                        <strong className="text-dark">Semestral</strong>
                        <small className="d-block text-secondary">Colunas após Jun e Dez</small>
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="summaryAnnually"
                        checked={summaryColumns.showAnnually}
                        onChange={(e) => setSummaryColumns(prev => ({ ...prev, showAnnually: e.target.checked }))}
                      />
                      <label className="form-check-label text-dark" htmlFor="summaryAnnually">
                        <i className="fas fa-calendar-check me-2 text-warning"></i>
                        <strong className="text-dark">Anual</strong>
                        <small className="d-block text-secondary">Coluna ao final do ano</small>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary" 
                    onClick={() => setSummaryColumns({ showQuarterly: false, showSemesterly: false, showAnnually: false })}
                  >
                    <i className="fas fa-times me-1"></i>Limpar Tudo
                  </button>
                  <button type="button" className="btn btn-info text-white" onClick={() => setShowSummaryModal(false)}>
                    <i className="fas fa-check me-1"></i>Aplicar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetMatrix;
